import { z } from "zod";
import { CrewIdParamSchema, StringToIntIdSchema } from "../common/dto";

/**
 * Refresh status enum
 */
export enum RefreshStatus {
  IDLE = "idle",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  ALREADY_RUNNING = "already_running",
}

/**
 * Schema for cache control query parameter
 * Transforms string "true"/"false" to boolean
 * Default is true (use cache)
 */
export const BaseIdsQuerySchema = z.object({
  baseIds: z.union([z.array(StringToIntIdSchema), StringToIntIdSchema.transform((v) => [v])]).optional(),
});

export type BaseIdsQuery = z.infer<typeof BaseIdsQuerySchema>;

export const CacheQuerySchema = z
  .object({
    useCache: z
      .enum(["true", "false"])
      .optional()
      .default("true")
      .transform((val) => val === "true"),
  })
  .merge(BaseIdsQuerySchema);

export type CacheQuery = z.infer<typeof CacheQuerySchema>;

/**
 * Schema for cache refresh query parameters
 * Transforms string "true"/"false" to boolean
 * @param reset - If true, ignores Redis status and starts refresh from the beginning (default: false)
 */
export const CacheRefreshQuerySchema = z.object({
  reset: z
    .enum(["true", "false"])
    .optional()
    .default("false")
    .transform((val) => val === "true"),
});

export type CacheRefreshQuery = z.infer<typeof CacheRefreshQuerySchema>;

/**
 * Wraps a response schema with cache metadata
 * Creates a nested structure with data and optional cachedAt
 *
 * @example
 * validateResponse(cached(myResponseSchema))
 * // Response: { data: { ...originalResponse }, cachedAt?: "2025-10-27T10:30:45.123Z" }
 */
export const cached = <T extends z.ZodTypeAny>(schema: T) => {
  return z.object({
    data: schema,
    cachedAt: z.string().datetime().optional(),
  });
};

/**
 * TypeScript helper for cached responses
 */
export interface CachedResponse<T> {
  data: T;
  cachedAt?: string;
}

/**
 * Schema for cache refresh status response
 */
export const RefreshStatusResponseSchema = z.object({
  status: z.nativeEnum(RefreshStatus),
  progress: z.number().int().min(0).optional(),
  total: z.number().int().min(0).optional(),
  percentage: z.number().int().min(0).max(100).optional(),
  completedAt: z.string().datetime().optional(),
  error: z.string().optional(),
});

export type RefreshStatusResponse = z.infer<typeof RefreshStatusResponseSchema>;

/**
 * @deprecated Use RefreshStatusResponse instead
 */
export interface RefreshStatusInfo {
  status: RefreshStatus;
  progress?: number;
  total?: number;
  percentage?: number;
  completedAt?: string;
  error?: string;
}

/**
 * Cache prefix keys for all Redis cache types
 */
export enum CachePrefix {
  USER_COMPLIANCE_WITH_COURSES = "user-compliance-with-courses",
  AIRCRAFT_OVERVIEW = "aircraft-overview",
}

export enum RefreshableCachePrefix {
  USER_COMPLIANCE_WITH_COURSES = CachePrefix.USER_COMPLIANCE_WITH_COURSES,
}

/**
 * Schema for cache type parameter validation
 */
export const CacheTypeParamsSchema = z.object({
  cacheType: z.enum(RefreshableCachePrefix),
});
export type CacheTypeParams = z.infer<typeof CacheTypeParamsSchema>;

export const CrewCacheTypeParamsSchema = z.object({
  ...CrewIdParamSchema.shape,
  ...CacheTypeParamsSchema.shape,
});
export type CrewCacheTypeParams = z.infer<typeof CrewCacheTypeParamsSchema>;
